import { TppWebFormMultibankingApiProviderEnum } from "@rentablo/api/apiv1";

export type BankImportFlow = "import" | "update";

export type PendingBankImport = {
  bankConnectionIds: number[];
  provider: TppWebFormMultibankingApiProviderEnum;
  flow: BankImportFlow;
  startedAt: number;
};

const STORAGE_KEY = "rentablo-ui-pendingBankImport";
// Written by the bundled package's in-app wealthAPI flow. The web form runs on
// another origin, so for imports started there only the return url reaches us.
const LEGACY_STORAGE_KEY = "wealthAPI-bankConnectionInProgress";
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

const RETURN_PARAMS = [
  "bankConnectionId",
  "bankConnectionType",
  "flow",
  "isEarlyRedirect",
];

const isProvider = (
  value: string | null | undefined
): value is TppWebFormMultibankingApiProviderEnum =>
  value === TppWebFormMultibankingApiProviderEnum.FIN_API ||
  value === TppWebFormMultibankingApiProviderEnum.WEALTH_API;

const toIds = (values: (string | number)[]) =>
  values.map(Number).filter((id) => Number.isInteger(id) && id > 0);

const toFlow = (value: string | null | undefined): BankImportFlow =>
  value === "update" ? "update" : "import";

const isFresh = (
  startedAt: number | undefined,
  now: number
): startedAt is number =>
  typeof startedAt === "number" && now - startedAt < MAX_AGE_MS;

// Storage can be blocked or full. A failed read means nothing is pending; a
// failed write still leaves the import watched for as long as the page is open.
const readStorage = (key: string) => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeStorage = (key: string, value: string) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // see readStorage
  }
};

const removeStorage = (key: string) => {
  try {
    localStorage.removeItem(key);
  } catch {
    // see readStorage
  }
};

const parseJson = <T>(raw: string | null): Partial<T> | null => {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

/**
 * What the web form appends to the url it sends the user back to. finAPI adds
 * one `bankConnectionId` per interface it imported.
 */
export const parseBankImportReturn = (
  params: URLSearchParams,
  now = Date.now()
): PendingBankImport | null => {
  const provider = params.get("bankConnectionType");
  const bankConnectionIds = toIds(params.getAll("bankConnectionId"));
  if (!isProvider(provider) || !bankConnectionIds.length) return null;

  return {
    bankConnectionIds,
    provider,
    flow: toFlow(params.get("flow")),
    startedAt: now,
  };
};

export const stripBankImportReturn = (params: URLSearchParams) => {
  RETURN_PARAMS.forEach((name) => params.delete(name));
  return params;
};

const readLegacyPendingBankImport = (now: number): PendingBankImport | null => {
  const legacy = parseJson<{
    id: number;
    type: BankImportFlow;
    startedAt: number;
  }>(readStorage(LEGACY_STORAGE_KEY));
  const bankConnectionIds = toIds(legacy?.id ? [legacy.id] : []);
  if (!legacy || !bankConnectionIds.length) return null;

  // Older package versions wrote no timestamp. Stamp one, as the package
  // does, or the entry would start its day over on every read.
  const startedAt = legacy.startedAt ?? now;
  if (legacy.startedAt === undefined) {
    writeStorage(LEGACY_STORAGE_KEY, JSON.stringify({ ...legacy, startedAt }));
  }
  if (!isFresh(startedAt, now)) return null;

  return {
    bankConnectionIds,
    provider: TppWebFormMultibankingApiProviderEnum.WEALTH_API,
    flow: toFlow(legacy.type),
    startedAt,
  };
};

export const readPendingBankImport = (
  now = Date.now()
): PendingBankImport | null => {
  const stored = parseJson<PendingBankImport>(readStorage(STORAGE_KEY));
  const bankConnectionIds = toIds(stored?.bankConnectionIds ?? []);

  if (
    stored &&
    bankConnectionIds.length &&
    isProvider(stored.provider) &&
    isFresh(stored.startedAt, now)
  ) {
    return {
      bankConnectionIds,
      provider: stored.provider,
      flow: toFlow(stored.flow),
      startedAt: stored.startedAt,
    };
  }

  return readLegacyPendingBankImport(now);
};

export const writePendingBankImport = (pending: PendingBankImport) =>
  writeStorage(STORAGE_KEY, JSON.stringify(pending));

export const clearPendingBankImport = () => {
  removeStorage(STORAGE_KEY);
  removeStorage(LEGACY_STORAGE_KEY);
};
